import { FormJourneyItem, LeadForm } from "@plura/shared/types";
import { AppError } from "@plura/shared/utils";
import { evaluateLogicItem } from "./evaluateLogicItem";

interface GetNextJourneyItemParams {
  leadForm: LeadForm;
  currentJourneyItemId: string;
  userResponses: Record<string, string>;
  isFinalJourneyItem?: boolean;
}

interface NextJourneyItemResult {
  nextJourneyItem: FormJourneyItem | null;
  assistantResponse: {
    content: string;
    expense: number;
  };
}

const getNextJourneyItemFromLogic = (
  journeyItems: FormJourneyItem[],
  currentJourneyItem: FormJourneyItem,
  userResponses: Record<string, string>,
): FormJourneyItem | null => {
  if (!currentJourneyItem.logicItems?.length) {
    return null;
  }

  const matchingLogicItem = currentJourneyItem.logicItems.find(
    logicItem => evaluateLogicItem(logicItem, userResponses)
  );

  if (!matchingLogicItem) return null;

  // If logic says to end on completion, return null to finish the form
  if (matchingLogicItem.endOnCompletion) {
    return null;
  }

  if (!matchingLogicItem.targetBlockId) return null;

  return journeyItems.find(item => item.blockId === matchingLogicItem.targetBlockId) ?? null;
};

const getNextSequentialItem = (
  journeyItems: FormJourneyItem[],
  currentJourneyItem: FormJourneyItem
): FormJourneyItem | null => {
  if (currentJourneyItem.endOnCompletion) return null;

  if (currentJourneyItem.targetBlockId) {
    const targetItem = journeyItems.find(item => item.blockId === currentJourneyItem.targetBlockId);

    if (targetItem) return targetItem;
  }

  return journeyItems.find(item => item.order === currentJourneyItem.order + 1) ?? null;
};

export const getNextJourneyItem = ({
  leadForm,
  currentJourneyItemId,
  userResponses,
  isFinalJourneyItem,
}: GetNextJourneyItemParams): NextJourneyItemResult => {
  if (!leadForm?.journeyItems?.length) {
    throw new AppError({
      message: 'Journey items not found when getting next journey item',
      code: 'NOT_FOUND',
    });
  }

  if (isFinalJourneyItem) {
    return {
      nextJourneyItem: null,
      assistantResponse: {
        expense: 0,
        content: '',
      },
    };
  }

  const journeyItems = leadForm.journeyItems.sort((a, b) => a.order - b.order);
  const currentJourneyItem = journeyItems.find(item => item.id === currentJourneyItemId);

  if (!currentJourneyItem) {
    throw new AppError({
      message: 'Current journey item not found when getting next journey item',
      code: 'NOT_FOUND',
    });
  }

  // Find next journey item based on logic or sequential order
  const nextJourneyItem =
    getNextJourneyItemFromLogic(journeyItems, currentJourneyItem, userResponses)
    ?? getNextSequentialItem(journeyItems, currentJourneyItem);

  return {
    nextJourneyItem: nextJourneyItem ? FormJourneyItem.parse(nextJourneyItem) : null,
    assistantResponse: {
      expense: 0,
      content: nextJourneyItem?.content ?? '',
    },
  };
};